import { Injectable, NgZone } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class LogoutTimerService {
  private timeoutId: number | null = null;

  constructor(private ngZone: NgZone) {}

  startLogoutAtMidnight(logoutCallback: () => void): void {
    this.clearTimer();
    
    const millisecondsUntilMidnight = this.calculateMillisecondsUntilMidnight();

    this.ngZone.runOutsideAngular(() => {
      this.timeoutId = window.setTimeout(() => {
        this.ngZone.run(() => {
          const isInWindow = this.isWithinLogoutWindow();
          
          if (isInWindow) {
            logoutCallback();
          } else {
            this.startLogoutAtMidnight(logoutCallback);
          }
        });
      }, millisecondsUntilMidnight);
    });
  }

  stopLogoutTimer(): void {
    this.clearTimer();
  }

  startLogoutTimerForTesting(delayMs: number, logoutCallback: () => void): void {
    this.clearTimer();
    
    this.ngZone.runOutsideAngular(() => {
      this.timeoutId = window.setTimeout(() => {
        this.ngZone.run(() => {
          logoutCallback();
        });
      }, delayMs);
    });
  }

  getTimerStatus(): { isActive: boolean; nextLogout?: string } {
    const isActive = this.timeoutId !== null;
    const status: { isActive: boolean; nextLogout?: string } = { isActive };
    
    if (isActive) {
      status.nextLogout = this.getNextMidnight().toLocaleString('en-US', { 
        timeZone: 'America/New_York',
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      }) + ' - 12:05 AM ET';
    }
    
    return status;
  }

  private calculateMillisecondsUntilMidnight(): number {
    const now = new Date();
    
    // Get current Eastern time
    const easternTimeString = now.toLocaleString('en-US', { 
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
    
    const [datePart, timePart] = easternTimeString.split(', ');
    const [month, day, year] = datePart.split('/');
    const [hour, minute, second] = timePart.split(':');
    
    const currentEasternTime = new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${hour}:${minute}:${second}`);
    const currentHour = currentEasternTime.getHours();
    const currentMinute = currentEasternTime.getMinutes();
    
    if (currentHour === 0 && currentMinute >= 0 && currentMinute <= 5) {
      return 100;
    }
    
    const nextMidnight = this.getNextMidnight();
    return nextMidnight.getTime() - now.getTime();
  }

  private isWithinLogoutWindow(): boolean {
    const now = new Date();
    
    const easternTimeString = now.toLocaleString('en-US', { 
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
    
    const [datePart, timePart] = easternTimeString.split(', ');
    const [month, day, year] = datePart.split('/');
    const [hour, minute, second] = timePart.split(':');
    
    const currentEasternTime = new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${hour}:${minute}:${second}`);
    const currentHour = currentEasternTime.getHours();
    const currentMinute = currentEasternTime.getMinutes();
    
    const isInWindow = currentHour === 0 && currentMinute >= 0 && currentMinute <= 5;
    
    return isInWindow;
  }

  private getNextLogoutWindow(): Date {
    const now = new Date();
    
    const easternTimeString = now.toLocaleString('en-US', { 
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
    
    const [datePart, timePart] = easternTimeString.split(', ');
    const [month, day, year] = datePart.split('/');
    const [hour, minute, second] = timePart.split(':');
    
    const currentEasternTime = new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${hour}:${minute}:${second}`);
    
    const next3AM = new Date(currentEasternTime);
    next3AM.setHours(0, 0, 0, 0);
    
    if (currentEasternTime.getTime() >= next3AM.getTime()) {
      next3AM.setDate(next3AM.getDate() + 1);
    }
    
    const easternOffset = this.getEasternTimeOffset();
    const nextMidnightUTC = new Date(next3AM.getTime() + (easternOffset * 60 * 1000));
    
    return nextMidnightUTC;
  }

  private getNextMidnight(): Date {
    const now = new Date();
    
    const easternTimeString = now.toLocaleString('en-US', { 
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
    
    const [datePart, timePart] = easternTimeString.split(', ');
    const [month, day, year] = datePart.split('/');
    const [hour, minute, second] = timePart.split(':');
    
    const currentEasternTime = new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${hour}:${minute}:${second}`);
    
    const nextMidnight = new Date(currentEasternTime);
    nextMidnight.setHours(0, 0, 0, 0);
    if (currentEasternTime.getTime() >= nextMidnight.getTime()) {
      nextMidnight.setDate(nextMidnight.getDate() + 1);
    }
    
    const easternOffset = this.getEasternTimeOffset();
    const nextMidnightUTC = new Date(nextMidnight.getTime() + (easternOffset * 60 * 1000));
    
    return nextMidnightUTC;
  }

  private getEasternTimeOffset(): number {
    const now = new Date();
    
    const easternTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
    const utcTime = new Date(now.toUTCString());
    
    const offsetMs = utcTime.getTime() - easternTime.getTime();
    return offsetMs / (1000 * 60);
  }

  private formatTimeRemaining(milliseconds: number): string {
    const hours = Math.floor(milliseconds / (1000 * 60 * 60));
    const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((milliseconds % (1000 * 60)) / 1000);
    
    return `${hours}h ${minutes}m ${seconds}s`;
  }

  private clearTimer(): void {
    if (this.timeoutId !== null) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  ngOnDestroy(): void {
    this.clearTimer();
  }
}